// 9fbef606107a605d69c0edbcd8029e5d
import { getVideoUrl, isClient } from '@tcp/core/src/utils/utils';
import { getItemAvailability } from './plpTileCommonHelpers';
import { getPLPProductAttributes } from '../../../../../../utils/badge.util';
import { IMAGES_BADGE_PARAMS_PATH } from '../../../../../../constants/brandSpecific.server.constants';

const STYLE_MAPS_PACK_SET = {
  '0002': 'MPACK',
  '0005': 'VirtualMPACK',
  '0003': 'Set',
  '0006': 'VirtualSet',
};
const BADGE_POSITION = 'g_west,w_0.22,fl_relative';

const getDprToUse = () => {
  const dprValue = isClient() && window && Math.floor(window.devicePixelRatio);
  if (dprValue === 2) return `dpr_1.5`;
  return dprValue ? `dpr_${dprValue}` : `dpr_1.5`;
};

function altImageArray(imageName, altImg) {
  try {
    const altImges = JSON.parse(altImg);
    return altImges[imageName].split(',').filter((img) => img);
  } catch (error) {
    return [];
  }
}

export const badgeTranformationPrefix = (styleType, brandId) => {
  const badgePath = IMAGES_BADGE_PARAMS_PATH[brandId?.toUpperCase()];
  if (['0003', '0006'].includes(styleType)) return `${badgePath}set`;
  if (['0002', '0005'].includes(styleType)) return `${badgePath}pack`;
  return null;
};

export const getBadgedUrl = (imgData) => {
  const attributes = getPLPProductAttributes();
  const styleQty = imgData[attributes.styleQty];
  const styleType = `${imgData[attributes.styleType]}`;
  const { primaryBrand } = imgData;
  const dynamicBadgeOverlayQty = STYLE_MAPS_PACK_SET[styleType] ? styleQty : 0;
  const badgeTranformation = badgeTranformationPrefix(styleType, primaryBrand);
  if (dynamicBadgeOverlayQty && badgeTranformation) {
    return `${badgeTranformation}${dynamicBadgeOverlayQty},${BADGE_POSITION}`;
  }
  return null;
};

export const getFullImageUrl = ({ assetHostBaseUrl, source }) => {
  const updatedSource = source;
  return `${assetHostBaseUrl}/image/upload/${updatedSource}`;
};

export const getFullVideoUrl = ({ assetHostBaseUrl, source }) => {
  const updatedSource = source.replace(/\.\w{3,4}$/i, '');
  return `${assetHostBaseUrl}/video/upload/${updatedSource}.webp`;
};

const getImageData = (imgData) => {
  const {
    stylePartNo,
    assetPath,
    imageName,
    altImage,
    isShortImage,
    productPartNo,
    assetHostBaseUrl,
  } = imgData;

  let imageStr = imageName || productPartNo;
  if (isShortImage) {
    const altImages = altImageArray(imageName, altImage);
    if (altImages.length > 0) {
      const shortImg = altImages.find((img) => img.toLowerCase().includes('-s'));
      if (shortImg) imageStr = shortImg;
    }
  }

  const overlayBadgeParams = getBadgedUrl(imgData);
  const imageUrl = `${assetPath}/${stylePartNo}/${imageStr}`;

  const { itemAvailability } = getItemAvailability();
  const isVideoUrl = getVideoUrl(imageUrl);
  const fullImageUrl = isVideoUrl
    ? getFullVideoUrl({ assetHostBaseUrl, source: imageUrl })
    : getFullImageUrl({ assetHostBaseUrl, source: imageUrl });
  return {
    itemAvailability,
    imageUrl,
    isVideoUrl,
    fullImageUrl,
    overlayBadgeParams,
  };
};

export const getPDPFullImageURL = (imageUrl, assetHostBaseUrl) => {
  if (!imageUrl || !assetHostBaseUrl) {
    return ''; // Return an empty string or a default URL
  }
  const isVideoUrl = getVideoUrl(imageUrl);
  return isVideoUrl
    ? getFullVideoUrl({ assetHostBaseUrl, source: imageUrl })
    : getFullImageUrl({ assetHostBaseUrl, source: imageUrl });
};

export const getImageSourceString = ({
  assetHostBaseUrl,
  overlayParamaString,
  compression,
  src,
  tname,
  isDamImageV2EnabledPLP,
  imageConfigWithoutDPR = '',
}) => {
  const dprToUse = imageConfigWithoutDPR || getDprToUse();
  return isDamImageV2EnabledPLP
    ? [
        `${assetHostBaseUrl}/image/upload/${overlayParamaString}${tname}${compression},${dprToUse}/${src}`,
      ]
    : [
        `${assetHostBaseUrl}/image/upload/${overlayParamaString}${tname}${compression}/${src} 1x`,
        `${assetHostBaseUrl}/image/upload/${overlayParamaString}${tname}${compression},dpr_1.5/${src} 2x`,
        `${assetHostBaseUrl}/image/upload/${overlayParamaString}${tname}${compression},dpr_3.0/${src} 3x`,
      ].join(',');
};
export const getImageSourceStringAPLP = ({ aplpImageUrl, tname }) => {
  const {
    assetHost,
    altImage,
    assetSuffix,
    productBrand,
    productIdNumber,
    badgeNumber,
    badgeTransformation,
    imageBorderAPLP,
    imageName,
    imageBgColor,
    flLayer,
    west,
    northEast,
    southEast,
    bgColorDimension,
    imageDimension,
  } = aplpImageUrl;
  return [
    `${assetHost}/w_312,h_257,c_scale,f_auto,dpr_1.0${imageBgColor}${flLayer},w_207,h_257${west}${assetSuffix}${productBrand}:${productIdNumber}:${imageName}${flLayer},${tname},f_auto,w_204,h_255,${imageBorderAPLP}${west},dpr_1.0${
      altImage[0]
        ? `${imageBgColor}${flLayer}${bgColorDimension}${northEast}${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}${flLayer},${tname}${imageDimension},${imageBorderAPLP},dpr_1.0${northEast}`
        : ''
    }${
      altImage[1]
        ? `${imageBgColor}${flLayer}${bgColorDimension}${southEast}${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}${flLayer},${tname}${imageDimension},${imageBorderAPLP},dpr_1.0${southEast}`
        : ''
    }${
      badgeNumber ? `/${badgeTransformation}${badgeNumber}${west},w_44,fl_relative` : ''
    }/bg/gym.jpg 1x`,
    `${assetHost}/w_312,h_257,c_scale,f_auto,dpr_1.5${imageBgColor}${flLayer},w_207,h_257${west}${assetSuffix}${productBrand}:${productIdNumber}:${imageName}${flLayer},${tname},f_auto,w_204,h_255,${imageBorderAPLP}${west},dpr_1.5${
      altImage[0]
        ? `${imageBgColor}${flLayer}${bgColorDimension}${northEast}${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}${flLayer},${tname}${imageDimension},${imageBorderAPLP},dpr_1.5${northEast}`
        : ''
    }${
      altImage[1]
        ? `${imageBgColor}${flLayer}${bgColorDimension}${southEast}${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}${flLayer},${tname}${imageDimension},${imageBorderAPLP},dpr_1.5${southEast}`
        : ''
    }${
      badgeNumber ? `/${badgeTransformation}${badgeNumber}${west},w_44,fl_relative` : ''
    }/bg/gym.jpg 2x`,
    `${assetHost}/w_312,h_257,c_scale,f_auto,dpr_3.0${imageBgColor}${flLayer},w_207,h_257${west}${assetSuffix}${productBrand}:${productIdNumber}:${imageName}${flLayer},${tname},f_auto,w_204,h_255,${imageBorderAPLP}${west},dpr_3.0${
      altImage[0]
        ? `${imageBgColor}${flLayer}${bgColorDimension}${northEast}${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}${flLayer},${tname}${imageDimension},${imageBorderAPLP},dpr_3.0${northEast}`
        : ''
    }${
      altImage[1]
        ? `${imageBgColor}${flLayer}${bgColorDimension}${southEast}${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}${flLayer},${tname}${imageDimension},${imageBorderAPLP},dpr_3.0${southEast}`
        : ''
    }${
      badgeNumber ? `/${badgeTransformation}${badgeNumber}${west},w_44,fl_relative` : ''
    }/bg/gym.jpg 3x`,
  ].join(',');
};

export const getImageSourceStringAPLPWithScale = ({ aplpImageUrl, tname }) => {
  const {
    assetHost,
    altImage,
    assetPrefix,
    assetSuffix,
    productBrand,
    productIdNumber,
    productId,
    badgeNumber,
    badgeTransformation,
    imageBorderAPLP,
  } = aplpImageUrl;
  return [
    `${assetHost}/w_788,h_649,c_scale,f_auto,q_auto${
      altImage[0]
        ? `${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/${tname}${assetPrefix}north_east`
        : ''
    }${
      altImage[1]
        ? `${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/${tname}${assetPrefix}south_east`
        : ''
    }${assetSuffix}${productBrand}:${productIdNumber}:${productId}.jpg/${tname},f_auto,q_auto,${imageBorderAPLP},fl_layer_apply,g_west${
      badgeNumber ? `/${badgeTransformation}${badgeNumber},g_west,w_0.14,fl_relative` : ''
    }/w_312,dpr_1.0/bg/gym.jpg 1x`,
    `${assetHost}/w_788,h_649,c_scale,f_auto,q_auto${
      altImage[0]
        ? `${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/${tname}${assetPrefix}north_east`
        : ''
    }${
      altImage[1]
        ? `${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/${tname}${assetPrefix}south_east`
        : ''
    }${assetSuffix}${productBrand}:${productIdNumber}:${productId}.jpg/${tname},f_auto,q_auto,${imageBorderAPLP},fl_layer_apply,g_west${
      badgeNumber ? `/${badgeTransformation}${badgeNumber},g_west,w_0.14,fl_relative` : ''
    }/w_312,dpr_1.5/bg/gym.jpg 2x`,
    `${assetHost}/w_788,h_649,c_scale,f_auto,q_auto${
      altImage[0]
        ? `${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/${tname}${assetPrefix}north_east`
        : ''
    }${
      altImage[1]
        ? `${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/${tname}${assetPrefix}south_east`
        : ''
    }${assetSuffix}${productBrand}:${productIdNumber}:${productId}.jpg/${tname},f_auto,q_auto,${imageBorderAPLP},fl_layer_apply,g_west${
      badgeNumber ? `/${badgeTransformation}${badgeNumber},g_west,w_0.14,fl_relative` : ''
    }/w_312,dpr_3.0/bg/gym.jpg 3x`,
  ].join(',');
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getImageSourceStringAPLPNewTrans = ({ aplpImageUrl, isDamImageV2EnabledPLP }) => {
  const {
    assetHost,
    altImage,
    assetSuffix,
    productBrand,
    productIdNumber,
    badgeNumber,
    badgeTransformation,
    imageBorderAPLP,
    imageName,
    imageBgColor,
    flLayer,
    west,
    northEast,
    southEast,
  } = aplpImageUrl;
  const dprToUse = getDprToUse();
  return isDamImageV2EnabledPLP
    ? [
        `${assetHost}/t_aplp_img,c_scale,f_auto,q_auto,${dprToUse}${imageBgColor}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,x_2${west}${assetSuffix}${productBrand}:${productIdNumber}:${imageName}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,${imageBorderAPLP}${west}${
          altImage[0]
            ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${northEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${northEast}`
            : ''
        }${
          altImage[1]
            ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${southEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${southEast}`
            : ''
        }${
          badgeNumber ? `/${badgeTransformation}${badgeNumber}${west},w_0.14,fl_relative` : ''
        }/bg/gym.jpg`,
      ]
    : [
        `${assetHost}/t_aplp_img,c_scale,f_auto,q_auto,dpr_1.0${imageBgColor}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,x_2${west}${assetSuffix}${productBrand}:${productIdNumber}:${imageName}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,${imageBorderAPLP}${west}${
          altImage[0]
            ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${northEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${northEast}`
            : ''
        }${
          altImage[1]
            ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${southEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${southEast}`
            : ''
        }${
          badgeNumber ? `/${badgeTransformation}${badgeNumber}${west},w_0.14,fl_relative` : ''
        }/bg/gym.jpg 1x`,
        `${assetHost}/t_aplp_img,c_scale,f_auto,q_auto,dpr_1.5${imageBgColor}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,x_2${west}${assetSuffix}${productBrand}:${productIdNumber}:${imageName}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,${imageBorderAPLP}${west}${
          altImage[0]
            ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${northEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${northEast}`
            : ''
        }${
          altImage[1]
            ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${southEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${southEast}`
            : ''
        }${
          badgeNumber ? `/${badgeTransformation}${badgeNumber}${west},w_0.14,fl_relative` : ''
        }/bg/gym.jpg 2x`,
        `${assetHost}/t_aplp_img,c_scale,f_auto,q_auto,dpr_3.0${imageBgColor}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,x_2${west}${assetSuffix}${productBrand}:${productIdNumber}:${imageName}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,${imageBorderAPLP}${west}${
          altImage[0]
            ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${northEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${northEast}`
            : ''
        }${
          altImage[1]
            ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${southEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${southEast}`
            : ''
        }${
          badgeNumber ? `/${badgeTransformation}${badgeNumber}${west},w_0.14,fl_relative` : ''
        }/bg/gym.jpg 3x`,
      ].join(',');
};

export default getImageData;
